'use client'
import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Progress } from '@/components/ui/progress'
import { Particles } from './particles'
import { useTheme } from '@/context/ThemeContext'

const analysisFunSteps = [
  "Evaluating your communication clarity 🗣️",
  "Measuring your persuasiveness factor 💼",
  "Analyzing how well you handled tough questions 🤔",
  "Calculating your confidence quotient 📊",
  "Checking for filler words and hesitations 🔍",
  "Evaluating your pitch structure and flow 🌊",
  "Measuring your enthusiasm level 🔥",
  "Analyzing your market knowledge depth 📚",
  "Evaluating your competitive awareness 👀",
  "Calculating your overall pitch effectiveness score ⭐",
]

interface InterviewAnalysisProgressProps {
  onComplete: (success: boolean) => void;
}

export const InterviewAnalysisProgress: React.FC<InterviewAnalysisProgressProps> = ({ 
  onComplete 
}) => {
  const { theme } = useTheme()
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [color, setColor] = useState(theme === 'dark' ? '#ffffff' : '#000000')

  useEffect(() => {
    const intervalId = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 1;
        if (newProgress >= 100) {
          clearInterval(intervalId);
          setTimeout(() => onComplete(true), 500);
          return 100;
        }
        return newProgress;
      });
      
      if (progress % 10 === 0) {
        setCurrentStep(prev => (prev + 1) % analysisFunSteps.length);
      }
    }, 150);

    return () => clearInterval(intervalId);
  }, [onComplete, progress]);

  useEffect(() => {
    setColor(theme === 'dark' ? '#ffffff' : '#000000');
  }, [theme]);

  return (
    <div className="fixed inset-0 bg-white dark:bg-zinc-950 z-50 flex items-center justify-center overflow-hidden">
      <Particles
        className="absolute inset-0"
        quantity={200}
        ease={80}
        color={color}
        staticity={50}
        size={0.3}
      />
      <div className="relative z-10 max-w-xl w-full text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Analyzing Your Interview
          </h1>
          <motion.p
            key={currentStep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="text-lg text-gray-600 dark:text-gray-300 mb-8"
          >
            {analysisFunSteps[currentStep]}
          </motion.p>
          <div className="w-80 mx-auto">
            <Progress value={progress} className="h-2" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {progress}% Complete
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
